/* GFI Nexus — Organic Certificates Summary PDF
   ─────────────────────────────────────────────────────────────────
   Builds a client-facing "Organic Certificates Summary" document as a
   PDF Blob. It reuses the official GFI look from the Invoice / Sales
   Report templates: navy #1B3A4B accents, the shared LOGO_B64 raster
   logo (height 100px), and the "Ground Fresh International, Inc. ·
   Lindenhurst, NY · ground-fresh.com" footer.

   The certificate table carries EXACTLY the same columns as the audit
   report in CertificationRegistry: Entity, Shipper, Certification type,
   Operation category, Certifying body, IDs (Cert# / Audit#), Dates
   (Audit / Valid from / Valid to), Certified hectares, Status.

   Rendering, like the other official docs, is done offscreen with
   html2canvas → jsPDF via htmlToPdfBlob (see pdfRenderer.js).

   This module ONLY builds the PDF; it does not touch the send/email UI. */

import { htmlToPdfBlob } from "./pdfRenderer";
import { LOGO_B64 } from "./components/SalesReports";

const NAVY = "#1B3A4B";
const FOOTER = "Ground Fresh International, Inc. · Lindenhurst, NY · ground-fresh.com";

/* Escape user / data strings before dropping them into the HTML. */
function esc(v) {
  if (v === undefined || v === null) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/* Format a date to MM/DD/YY (U.S. standard, per project rules).
   Accepts "YYYY-MM-DD", a Date, or a parseable string. Returns "—"
   when empty / unparseable. */
function fmtMDY(d) {
  if (!d) return "—";
  /* Fast path for the canonical "YYYY-MM-DD" storage format so we
     don't drift a day from timezone parsing. */
  if (typeof d === "string") {
    const p = d.split("-");
    if (p.length === 3 && p[0].length === 4) {
      const yy = p[0].slice(2);
      return `${p[1]}/${p[2]}/${yy}`;
    }
  }
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return typeof d === "string" ? d : "—";
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const yy = String(dt.getFullYear()).slice(2);
  return `${mm}/${dd}/${yy}`;
}

/* Certification-type label. The stored value is "organic" / "foodSafety". */
function certTypeLabel(c) {
  if (c.certTypeLabel) return c.certTypeLabel;
  if (c.certType === "organic") return "Organic";
  if (c.certType === "foodSafety" || c.certType === "food_safety") return "Food Safety";
  return c.certType || "—";
}

/* Entity display name. Prefer a precomputed name (the caller can map the
   report's getEntityName result), fall back to raw fields. */
function entityName(c) {
  return c.entityName || c.entity || c.entityId || "—";
}

/* Operation category = the certification subtype. */
function operationCategory(c) {
  return c.subtypeLabel || c.operationCategory || c.subtype || "—";
}

/* Certifying body. Mirrors the report's "__other__" handling. */
function certifyingBody(c) {
  if (c.certifier === "__other__") return c.certifierOther || "—";
  return c.certifier || c.certifyingBody || "—";
}

/* IDs cell — Cert #, NOP ID, Audit #, one per line (as in the report). */
function idsCell(c) {
  const parts = [
    c.certNumber ? `Cert: ${esc(c.certNumber)}` : "",
    c.nopId ? `NOP: ${esc(c.nopId)}` : "",
    c.auditNumber ? `Audit#: ${esc(c.auditNumber)}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join("<br>") : "—";
}

/* Dates cell. Organic certs use Inspection / Effective / Issue /
   Anniversary; food-safety uses Audit / Valid from / Valid to — same
   split the audit report uses. */
function datesCell(c) {
  const lbl = (t, v, bold) =>
    `<div><small style="color:#888">${t}:</small> ${bold ? "<b>" + fmtMDY(v) + "</b>" : fmtMDY(v)}</div>`;
  if (c.certType === "organic") {
    return (
      lbl("Inspection", c.inspectionDate) +
      lbl("Effective", c.effectiveDate) +
      lbl("Issue", c.issueDate) +
      lbl("Anniversary", c.anniversaryDate, true)
    );
  }
  return (
    lbl("Audit", c.auditDate) +
    lbl("Valid from", c.validFrom) +
    lbl("Valid to", c.validTo, true)
  );
}

/* Certified hectares — precomputed by the caller (computeTotalHectares)
   or a plain numeric field. */
function hectaresCell(c) {
  const ha = parseFloat(c.hectares != null ? c.hectares : c.totalHectares != null ? c.totalHectares : c.certifiedHectares);
  return ha > 0 ? `${ha} ha` : "—";
}

/* Status text + color. Prefer a precomputed label/color; otherwise
   derive a simple Active / Expired from the governing expiry date. */
function statusCell(c) {
  if (c.statusLabel) {
    return { label: c.statusLabel, color: c.statusColor || "#333" };
  }
  const expiry = c.certType === "organic" ? c.anniversaryDate : c.validTo;
  if (expiry) {
    const dt = new Date(expiry + (String(expiry).length === 10 ? "T00:00:00" : ""));
    if (!isNaN(dt.getTime())) {
      const expired = dt.getTime() < Date.now();
      return expired
        ? { label: "Expired", color: "#c0392b" }
        : { label: "Active", color: "#2e7d32" };
    }
  }
  return { label: "—", color: "#333" };
}

/* ─────────────────────────────────────────────────────────────────
   buildOrganicCertificatesSummaryPdf

   Args (object):
     - client        client record ({ full_name / fullName, company, ... })
     - contact       selected contact ({ name, email })
     - certifications array of cert records (as shown in the audit report)
     - notes         optional free text; section omitted when blank
     - sendDate      Date or string; rendered MM/DD/YY (defaults to today)
     - signerName    optional; when omitted the name line is left blank
     - signerTitle   optional; defaults to "Authorized Representative"

   Returns: Promise<Blob>  (application/pdf) */
export async function buildOrganicCertificatesSummaryPdf(args) {
  args = args || {};
  const client = args.client || {};
  const contact = args.contact || {};
  const certifications = Array.isArray(args.certifications) ? args.certifications : [];
  const notes = (args.notes || "").trim();

  const sendDate = args.sendDate ? fmtMDY(args.sendDate) : fmtMDY(new Date());

  /* Signature comes ONLY from the passed params (project rule 10: no
     localStorage). The caller supplies the real company_profile data.
     No signerName → leave the name blank; don't invent one. */
  const signerName = args.signerName || "";
  const signerTitle = args.signerTitle || "Authorized Representative";

  const clientName = client.full_name || client.fullName || client.company || client.short_name || client.shortName || "—";
  const contactName = contact.name || contact.contactName || "";
  const contactEmail = contact.email || "";

  /* Build the certificate rows. */
  /* 2026-09-28 (Arturo: "no debe informar a que shipper esta ligado,
     solo datos de growers") — this summary is client-facing (sent
     outside GFI); shipper linkage is internal-only info and never
     belongs on it, so there is no shipper column at all. */
  const rows = certifications.map((c) => {
    const st = statusCell(c);
    return `<tr>
      <td class="cell name">${esc(entityName(c))}</td>
      <td class="cell c">${esc(certTypeLabel(c))}</td>
      <td class="cell c">${esc(operationCategory(c))}</td>
      <td class="cell">${esc(certifyingBody(c))}</td>
      <td class="cell mono">${idsCell(c)}</td>
      <td class="cell dates">${datesCell(c)}</td>
      <td class="cell c ha">${esc(hectaresCell(c))}</td>
      <td class="cell c"><span class="status" style="color:${st.color}">${esc(st.label)}</span></td>
    </tr>`;
  }).join("");

  const emptyRow = `<tr><td class="cell c" colspan="8" style="color:#888;padding:16px">No certificates selected.</td></tr>`;

  /* ── Styles: same navy language as the Invoice template. ── */
  let h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Organic Certificates Summary</title><style>';
  h += '@page{size:letter landscape;margin:0.5in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:1000px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid ' + NAVY + '}';
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:26px;font-weight:300;color:' + NAVY + ';letter-spacing:2px;text-align:right}';
  h += '.doc-sub{font-size:10px;color:#888;text-align:right;margin-top:4px}';
  h += '.sent-to{margin:14px 0;padding:10px 16px;border:1px solid #e0e0e0;border-radius:6px;border-left:4px solid ' + NAVY + '}';
  h += '.sent-to .st-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.sent-to .st-name{font-size:13px;font-weight:700;color:' + NAVY + '}';
  h += '.sent-to .st-contact{font-size:10px;color:#666;margin-top:2px}';
  h += 'table{width:100%;border-collapse:collapse;margin-top:6px}';
  h += 'thead th{padding:7px 8px;font-size:8px;text-transform:uppercase;letter-spacing:0.5px;color:#fff;background:' + NAVY + ';font-weight:600;text-align:left;border:1px solid ' + NAVY + '}';
  h += 'thead th:first-child{border-radius:4px 0 0 0}thead th:last-child{border-radius:0 4px 0 0}';
  h += '.cell{padding:6px 8px;border:1px solid #ddd;font-size:10px;vertical-align:top}';
  h += '.cell.name{font-weight:500}';
  h += '.cell.c{text-align:center}';
  h += '.cell.mono{font-family:"Courier New",monospace;font-size:9px}';
  h += '.cell.dates{font-size:10px}';
  h += '.cell.ha{font-weight:600}';
  h += 'tbody tr:nth-child(even){background:#fafbfc}';
  h += '.status{padding:2px 8px;border-radius:10px;font-size:9px;font-weight:700}';
  h += '.notes{margin-top:18px;padding:12px 16px;background:#f7f8fa;border-radius:6px;border-left:4px solid ' + NAVY + '}';
  h += '.notes .n-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.notes .n-body{font-size:10px;color:#333;white-space:pre-wrap;line-height:1.5}';
  h += '.sign{margin-top:34px}';
  h += '.sign .s-company{font-size:12px;font-weight:700;color:' + NAVY + '}';
  h += '.sign .s-line{width:240px;border-top:1px solid #999;margin-top:34px;padding-top:4px}';
  h += '.sign .s-name{font-size:11px;font-weight:600;color:#1a1a1a}';
  h += '.sign .s-title{font-size:9px;color:#888}';
  h += '.footer{margin-top:24px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';

  /* ── Header ── */
  h += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
  h += '<div><div class="doc-title">Organic Certificates Summary</div>';
  h += '<div class="doc-sub">Date sent: <b>' + esc(sendDate) + '</b></div></div></div>';

  /* ── Sent to ── */
  h += '<div class="sent-to"><div class="st-label">Sent to</div>';
  h += '<div class="st-name">' + esc(clientName) + '</div>';
  if (contactName || contactEmail) {
    const contactLine = [esc(contactName), contactEmail ? esc(contactEmail) : ""].filter(Boolean).join(" · ");
    h += '<div class="st-contact">' + contactLine + '</div>';
  }
  h += '</div>';

  /* ── Certificates table ── */
  h += '<table><thead><tr>';
  h += '<th>Entity</th><th>Certification Type</th><th>Operation Category</th>';
  h += '<th>Certifying Body</th><th>IDs</th><th>Dates</th><th>Certified Hectares</th><th>Status</th>';
  h += '</tr></thead><tbody>' + (rows || emptyRow) + '</tbody></table>';

  /* ── Notes (omitted when empty) ── */
  if (notes) {
    h += '<div class="notes"><div class="n-label">Notes</div>';
    h += '<div class="n-body">' + esc(notes) + '</div></div>';
  }

  /* ── Signature ── */
  h += '<div class="sign">';
  h += '<div class="s-company">Ground Fresh International, Inc.</div>';
  h += '<div class="s-line">';
  if (signerName) h += '<div class="s-name">' + esc(signerName) + '</div>';
  h += '<div class="s-title">' + esc(signerTitle) + '</div>';
  h += '</div></div>';

  /* ── Footer ── */
  h += '<div class="footer">' + FOOTER + '</div>';

  h += '</div></body></html>';

  /* Landscape Letter so the 9-column table has room. */
  return htmlToPdfBlob(h, { widthPx: 1056, format: "letter", orientation: "landscape" });
}

export default buildOrganicCertificatesSummaryPdf;
